/**
 * USE-MANAGE-ITEMS-MODAL.JS - Manage Items Modal State Management Hook
 * 
 * SUMMARY:
 * Custom hook that manages the Manage Items modal state and logic.
 * Handles modal open/close, available items building, item selection state, and applying selections.
 */

import { useState } from 'react';
import { buildDescription, normaliseSubcontractorItem } from '../utils/work-order-helpers';

/**
 * Custom hook for managing Manage Items modal.
 * 
 * @param {Object} params - Hook parameters
 * @param {Array} params.selectedTasks - Currently selected tasks
 * @param {Array} params.selectedItems - Currently selected line items
 * @param {Object} params.taskByIdRef - Reference to taskById map
 * @param {Function} params.setSelectedItems - Function to update selected items
 * @returns {Object} Modal state and handlers
 * 
 * @example
 * const {
 *   modalOpen,
 *   openModal,
 *   closeModal,
 *   applySelection
 * } = useManageItemsModal({ selectedTasks, selectedItems, taskByIdRef, setSelectedItems });
 */
export const useManageItemsModal = ({
  selectedTasks,
  selectedItems,
  taskByIdRef,
  setSelectedItems
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [checkedItemsSet, setCheckedItemsSet] = useState(new Set());

  /**
   * Builds an array of all available subcontractor items from selected tasks.
   * Each item includes task and subtask context for display in the Manage Items modal.
   * Uses the taskId from existing selectedItems when available to ensure key consistency.
   * 
   * @returns {Array} Array of available items with structure:
   *   { taskId, taskName, taskType, subTaskId, subTaskName, 
   *     subcontractorItemId, subcontractorDescription, cost, amount }
   */
  const buildAvailableItems = () => {
    const items = [];

    // Create a lookup map for existing items by subTask + subcontractor item
    // This ensures we use the same taskId that's in the table
    const existingItemMap = new Map();
    selectedItems.forEach(item => {
      const lookupKey = `${item.subTaskId}-${item.subcontractorItemId}`;
      existingItemMap.set(lookupKey, item);
    });

    selectedTasks.forEach(selectedTask => {
      // Always use taskId to look up the actual task data
      const task = taskByIdRef.current.get(selectedTask.taskId);

      if (!task || !Array.isArray(task.subTasks)) return;

      task.subTasks.forEach(subTask => {
        if (!Array.isArray(subTask.subcontractor)) return;

        subTask.subcontractor.forEach(item => {
          const normalizedItem = normaliseSubcontractorItem(item);
          if (!normalizedItem.id) return;

          // Check if this item exists in the table
          const lookupKey = `${subTask.id}-${normalizedItem.id}`;
          const existingItem = existingItemMap.get(lookupKey);

          items.push({
            // Use existing taskId if item is in table, otherwise use stageId for attribution or taskId
            taskId: existingItem?.taskId ?? (selectedTask.stageId || selectedTask.taskId),
            // Use stageName for table attribution if present, otherwise use taskName
            taskName: existingItem?.taskName ?? (selectedTask.stageName || selectedTask.taskName),
            // Store full display name for modal (includes path context)
            displayName: selectedTask.taskName, // Full context like "SETOUT (LEVEL 5 > SETOUT)"
            taskType: existingItem?.taskType ?? selectedTask.taskType,
            // Pass through original task info for proper modal grouping
            // If no existing item, set original to the lookup task (WATER/FIRE INTERNAL)
            originalTaskId: existingItem?.originalTaskId ?? selectedTask.taskId,
            originalTaskName: existingItem?.originalTaskName ?? (task.name || selectedTask.taskName),
            // Preserve full stage path for hierarchical display in modal
            stagePath: existingItem?.stagePath ?? selectedTask.stagePath,
            subTaskId: subTask.id,
            subTaskName: subTask.description || '',
            subcontractorItemId: normalizedItem.id,
            subcontractorDescription: normalizedItem.description,
            cost: normalizedItem.Cost,
            amount: normalizedItem.saleAmount
          });
        });
      });
    });

    return items;
  };

  /**
   * Opens the Manage Items modal.
   * Builds available items from selected tasks and initializes checked state
   * based on items currently in the table.
   */
  const openModal = () => {
    // Build available items from selected tasks
    const items = buildAvailableItems();
    setAvailableItems(items);

    // Build initial checked set from existing selectedItems
    const initialChecked = new Set();
    selectedItems.forEach(item => {
      const key = `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`;
      initialChecked.add(key);
    });
    setCheckedItemsSet(initialChecked);

    setModalOpen(true);
  };

  /**
   * Closes the Manage Items modal without applying changes.
   */
  const closeModal = () => {
    setModalOpen(false);
  };

  /**
   * Toggles an item's checked state in the modal.
   * 
   * @param {string} itemKey - Composite key: `${taskId}-${subTaskId}-${subcontractorItemId}`
   * @param {boolean} checked - New checked state
   */
  const toggleItem = (itemKey, checked) => {
    setCheckedItemsSet(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(itemKey);
      } else {
        newSet.delete(itemKey);
      }
      return newSet;
    });
  };

  /**
   * Applies the selected items from the modal to the table.
   * Preserves existing customizations (descriptions, amounts, attributions) for items
   * that remain selected. Adds newly selected items with default values.
   */
  const applySelection = () => {
    const newSelectedItems = [];

    // Create a map of existing items for quick lookup (skip manual items)
    const existingItemsMap = new Map();
    selectedItems.forEach(item => {
      // Skip manual items when building the map (they have no subTaskId/subcontractorItemId)
      if (!item.isManual) {
        const key = `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`;
        existingItemsMap.set(key, item);
      }
    });

    // Build new selectedItems array from checked items
    availableItems.forEach(availableItem => {
      const key = `${availableItem.taskId}-${availableItem.subTaskId}-${availableItem.subcontractorItemId}`;

      if (checkedItemsSet.has(key)) {
        const existingItem = existingItemsMap.get(key);

        if (existingItem) {
          // Preserve existing item with all customizations
          newSelectedItems.push(existingItem);
        } else {
          // Add new item with default values
          const description = buildDescription(
            availableItem.taskName,
            availableItem.subTaskName,
            availableItem.subcontractorDescription
          );

          newSelectedItems.push({
            taskId: availableItem.taskId,
            taskName: availableItem.taskName,
            taskType: availableItem.taskType,
            subTaskId: availableItem.subTaskId,
            subTaskName: availableItem.subTaskName,
            subcontractorItemId: availableItem.subcontractorItemId,
            description: description,
            cost: availableItem.cost,
            amount: availableItem.amount
          });
        }
      }
    });

    // Preserve manual items by appending them after task-sourced items
    const manualItems = selectedItems.filter(it => it.isManual);
    const finalSelectedItems = [...newSelectedItems, ...manualItems];

    setSelectedItems(finalSelectedItems);
    setModalOpen(false);
  };

  return {
    modalOpen,
    availableItems,
    checkedItemsSet,
    openModal,
    closeModal,
    toggleItem,
    applySelection,
  };
};
